import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { jsonLdWithMedia } from "../seo/mediaSchema";
import { isPrerendering } from "../utils/isPrerendering";

const labVideos = [
  {
    src: "/videos/bluepath-robotics-amr-demo.mp4",
    poster: "/images/bluepath-robotics-amr-demo.jpg",
    title: "Robots arriving in the MAG Lab",
    caption: "Initial intake and setup before integration testing.",
  },
  {
    src: "/videos/linkedin-video-570kbps.mp4",
    poster: "/images/mag-lab-validation.jpg",
    title: "",
    caption: "MAG Lab footage.",
  },
  {
    src: "/videos/new_maglab_vids/linkedin-video-662kbps.mp4",
    poster: "/images/meet-mag.jpg",
    title: "Meet MAG",
    caption: "A look at MAG and the work in the MAG Lab.",
    withAudio: true,
  },
  {
    src: "/videos/new_maglab_vids/linkedin-video-671kbps.mp4",
    poster: "/images/kuka-demo.jpg",
    title: "KUKA demo",
    caption: "KUKA in the MAG Lab.",
  },
  {
    src: "/videos/new_maglab_vids/linkedin-video-683kbps.mp4",
    poster: "/images/wheelme-demo.jpg",
    title: "wheel.me demo",
    caption: "Wheel.me in the MAG Lab.",
  },
  {
    src: "/videos/linkedin-video-758kbps.mp4",
    poster: "/images/mag-lab-mixed-fleet.jpg",
    title: "",
    caption: "MAG Lab footage.",
  },
];

function LabShowcaseClip({
  src,
  poster,
  title,
  caption,
  featured = false,
  wide = false,
  withAudio = false,
}) {
  const videoRef = useRef(null);
  const height = featured
    ? "min-h-[18rem] sm:min-h-[26rem] lg:min-h-[34rem]"
    : "min-h-[16rem] sm:min-h-[18rem] lg:min-h-[22rem]";
  const label = title ? `${title}. ${caption}` : caption;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || isPrerendering()) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;

    const tryPlay = () => {
      el.loop = true;
      el.playsInline = true;
      el.muted = !withAudio;
      el.play().catch(() => {});
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay();
        else el.pause();
      },
      { rootMargin: "120px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [withAudio]);

  return (
    <figure
      className={`relative overflow-hidden bg-black ${height} ${
        featured ? "sm:col-span-2 lg:col-span-3" : wide ? "lg:col-span-2" : ""
      } ${withAudio ? "" : "mag-lab-clip-nochrome"}`}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 hidden h-full w-full object-cover motion-safe:block"
        src={src}
        poster={poster}
        muted={!withAudio}
        loop
        playsInline
        preload="none"
        controls={withAudio}
        aria-label={label}
      />
      {title ? (
        <figcaption
          className={`pointer-events-none absolute inset-x-0 z-20 px-4 ${
            withAudio
              ? "bottom-10 pb-1"
              : "bottom-0 bg-gradient-to-t from-black/80 to-transparent pb-4 pt-16"
          }`}
        >
          <h3
            className={`text-sm font-semibold tracking-tight text-white sm:text-base ${
              withAudio ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : ""
            }`}
          >
            {title}
          </h3>
        </figcaption>
      ) : null}
    </figure>
  );
}

export default function MagLab() {
  const labWork = [
    {
      title: "Commissioned before the plant sees it",
      description:
        "Routes, traffic, and handoffs are proved in the Lab, so the first day on site repeats a system that already runs.",
    },
    {
      title: "A multi-vendor fleet, commissioned as one system",
      description:
        "Several platforms and the fleet software around them are commissioned together.",
    },
    {
      title: "The same result, repeated on site",
      description:
        "What is tested and proven in the Lab is what gets commissioned on the floor.",
    },
  ];

  return (
    <div className="bg-white text-gray-900">
      <Seo
        title="MAG Lab | Free AMR Proof of Concept in Metro Detroit"
        description="A 10,000 square foot lab where a solution is tested, proven, and then demoed before it ships to a customer floor. Demos in Metro Detroit."
        path="/mag-lab"
        image="https://www.mag-automate.com/images/Image.jpg"
        jsonLd={jsonLdWithMedia(
          "/mag-lab",
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "MAG Lab", path: "/mag-lab" },
          ])
        )}
      />
      <section className="relative overflow-hidden bg-gray-950 py-20 text-white sm:py-28">
        <div className="absolute inset-0 bg-tech-grid opacity-30" aria-hidden="true" />
        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6">
          <p className="page-eyebrow-on-dark">The MAG Lab · Metro Detroit</p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-5xl">
            Test. Validate. Launch with confidence.
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-base text-white/75 sm:text-lg">
            A 10,000 square foot lab where a solution is tested, proven, and then demoed before it is asked to run next to live production.
          </p>
        </div>
      </section>

      <section className="bg-gray-950 pb-2" aria-labelledby="lab-showcase-heading">
        <h2 id="lab-showcase-heading" className="sr-only">
          Showcase
        </h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {labVideos.map((video, index) => (
            <LabShowcaseClip
              key={video.src}
              featured={index === 0}
              wide={index === labVideos.length - 1}
              {...video}
            />
          ))}
        </div>
      </section>

      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">What happens in the MAG Lab</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
            Commissioning starts from a system that already works, and that result can be repeated on site.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-16">
            {labWork.map(({ title, description }) => (
              <div key={title} className="border-t border-gray-900 pt-5">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">{description}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/contact" className="btn-primary-dark">
              Book a demo at the MAG Lab
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
